<template>
  <div>
    <header class="absolute inset-x-0 top-0 z-50 flex h-16 border-b border-gray-900/10">
    <div class="mx-auto flex w-full max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
      <div class="flex flex-1 items-center gap-x-6">
        <button
          type="button"
          class="-m-3 p-3 md:hidden"
          @click="mobileMenuOpen = true"
        >
          <span class="sr-only">Open main menu</span>
          <Bars3Icon
            class="h-5 w-5 text-gray-900"
            aria-hidden="true"
          />
        </button>
        <img
          class="h-8 w-auto"
          src="@/assets/kaizen2.png"
          alt="ExpatJobs Logo"
        />
      </div>
      <nav
        class="hidden md:flex md:gap-x-11 md:text-sm md:font-semibold md:leading-6 md:text-gray-700"
      >
        <a
          v-for="(item, itemIdx) in navigation"
          :key="itemIdx"
          :href="item.href"
          >{{ item.name }}</a
        >
      </nav>
      <div class="flex flex-1 items-center justify-end gap-x-8">

        <a
          href="/profile"
          class="-m-1.5 p-1.5"
        >
          <span class="sr-only">Your profile</span>
          <div class="h-8 w-8 rounded-full bg-red-100 flex items-center justify-center">
            <svg class="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
        </a>
      </div>
    </div>
    <Dialog
      as="div"
      class="lg:hidden"
      @close="mobileMenuOpen = false"
      :open="mobileMenuOpen"
    >
      <div class="fixed inset-0 z-50" />
      <DialogPanel
        class="fixed inset-y-0 left-0 z-50 w-full overflow-y-auto bg-white px-4 pb-6 sm:max-w-sm sm:px-6 sm:ring-1 sm:ring-gray-900/10"
      >
        <div class="-ml-0.5 flex h-16 items-center gap-x-6">
          <button
            type="button"
            class="-m-2.5 p-2.5 text-gray-700"
            @click="mobileMenuOpen = false"
          >
            <span class="sr-only">Close menu</span>
            <XMarkIcon
              class="h-6 w-6"
              aria-hidden="true"
            />
          </button>
          <div class="-ml-0.5">
            <a
              href=""
              class="-m-1.5 block p-1.5"
            >
              <span class="sr-only">Kaizen Flow</span>
              <img
                class="h-8 w-auto"
                src="@/assets/kaizen2.png"
                alt="ExpatJobs Logo"
              />
            </a>
          </div>
        </div>
        <div class="mt-2 space-y-2">
          <a
            v-for="item in navigation"
            :key="item.name"
            :href="item.href"
            class="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50"
            >{{ item.name }}</a
          >
        </div>
      </DialogPanel>
    </Dialog>
  </header>

  <div class="mx-auto max-w-7xl pt-16 lg:flex lg:gap-x-16 lg:px-8">
    <aside
      class="flex overflow-x-auto border-b border-gray-900/5 py-4 lg:block lg:w-64 lg:flex-none lg:border-0 lg:py-20"
    >
      <nav class="flex-none px-4 sm:px-6 lg:px-0">
        <ul
          role="list"
          class="flex gap-x-3 gap-y-1 whitespace-nowrap lg:flex-col"
        >
          <li
            v-for="item in secondaryNavigation"
            :key="item.name"
          >
            <a
              :href="item.href"
              :class="[
                item.current
                  ? 'bg-gray-50 text-indigo-600'
                  : 'text-gray-700 hover:text-indigo-600 hover:bg-gray-50',
                'group flex gap-x-3 rounded-md py-2 pl-2 pr-3 text-sm leading-6 font-semibold'
              ]"
            >
              <component
                :is="item.icon"
                :class="[
                  item.current ? 'text-indigo-600' : 'text-gray-400 group-hover:text-indigo-600',
                  'h-6 w-6 shrink-0'
                ]"
                aria-hidden="true"
              />
              {{ item.name }}
            </a>
          </li>
        </ul>

        <!-- Site Navigation Links -->
        <div class="mt-8 pt-6 border-t border-gray-200">
          <h3 class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
            Site Navigation
          </h3>
          <ul
            role="list"
            class="space-y-1"
          >
            <li
              v-for="item in siteNavigation"
              :key="item.name"
            >
              <a
                :href="item.href"
                target="_blank"
                rel="noopener noreferrer"
                class="group flex gap-x-3 rounded-md py-2 pl-2 pr-3 text-sm leading-6 text-gray-600 hover:text-indigo-600 hover:bg-gray-50"
              >
                <component
                  :is="item.icon"
                  :class="[
                    'text-gray-400 group-hover:text-indigo-600',
                    'h-5 w-5 shrink-0'
                  ]"
                  aria-hidden="true"
                />
                {{ item.name }}
              </a>
            </li>
          </ul>
        </div>
      </nav>
    </aside>

    <main class="px-4 py-16 sm:px-6 lg:flex-auto lg:px-0 lg:py-20">
      <router-view />
    </main>
  </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { Dialog, DialogPanel } from '@headlessui/vue'
import {
  UserCircleIcon,
  StarIcon,
  Bars3Icon,
  XMarkIcon,
  HomeIcon,
  BriefcaseIcon,
  UserGroupIcon,
  DocumentTextIcon,
  AcademicCapIcon,
  QuestionMarkCircleIcon,
  GlobeAltIcon
} from '@heroicons/vue/24/outline'

const navigation = [
  // { name: 'Admin Profile', href: '/admin/home' },
  // { name: 'Manager Portal', href: '/manager/home' },
  // { name: 'Human Resource Portal', href: '/human-resource/home' },
  // { name: 'Company News', href: '/admin/company-news' }
]
const secondaryNavigation = [
  { name: 'Dashboard', href: '/admin/dashboard', icon: UserCircleIcon, current: true },
  { name: 'Job Posts', href: '/admin/job-posts', icon: UserCircleIcon, current: false },
  { name: 'Saved Jobs', href: '/admin/bookmarks', icon: StarIcon, current: false }
]

const siteNavigation = [
  { name: 'Home', href: '/', icon: HomeIcon },
  { name: 'Browse Jobs', href: '/jobs', icon: BriefcaseIcon },
  { name: 'Post a Job', href: '/post-job', icon: DocumentTextIcon },
  { name: 'Community', href: '/community', icon: UserGroupIcon },
  { name: 'Career Advice', href: '/career-advice', icon: AcademicCapIcon },
  { name: 'About Us', href: '/about', icon: QuestionMarkCircleIcon },
  { name: 'Contact', href: '/contact', icon: GlobeAltIcon }
]

const mobileMenuOpen = ref(false)
// const automaticTimezoneEnabled = ref(true)
</script>
